import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8fb15f11-0979-40df-bd96-a7e47405b17c/artifacts/6fd4l3gr_Logo%20de%20claudtexa%20con%20fondo%20transparante.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    if (!user) {
      navigate('/login');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, user]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    setAttempts(attempt + 1);

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <Loader2 className="w-16 h-16 text-[#FF6B00] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Verificando pago...</h1>
            <p className="text-gray-400 mb-4">Intento {attempts} de 5</p>
          </>
        );
      
      case 'success':
        return (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">¡Pago exitoso!</h1>
            <p className="text-gray-400 mb-8">Tu cuenta ha sido actualizada correctamente.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/dashboard"
                className="btn-primary px-6 py-3 rounded-lg flex items-center justify-center gap-2"
                data-testid="go-to-dashboard-btn"
              >
                Ir al Dashboard
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        );
      
      case 'expired':
        return (
          <>
            <XCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Sesión expirada</h1>
            <p className="text-gray-400 mb-8">La sesión de pago ha expirado. Por favor, intenta de nuevo.</p>
            <Link
              to="/pricing"
              className="btn-primary px-6 py-3 rounded-lg inline-flex items-center gap-2"
            >
              Volver a Precios
            </Link>
          </>
        );
      
      case 'timeout':
        return (
          <>
            <Loader2 className="w-20 h-20 text-gray-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Procesando...</h1>
            <p className="text-gray-400 mb-8">
              El pago está siendo procesado. Revisa tu email para la confirmación.
            </p>
            <Link
              to="/dashboard"
              className="btn-secondary px-6 py-3 rounded-lg inline-flex items-center gap-2"
            >
              Ir al Dashboard
            </Link>
          </>
        );
      
      case 'error':
      default:
        return (
          <>
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Error en el pago</h1>
            <p className="text-gray-400 mb-8">
              Hubo un problema al procesar tu pago. Por favor, contacta soporte.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="btn-primary px-6 py-3 rounded-lg"
              >
                Intentar de nuevo
              </Link>
              <Link
                to="/"
                className="btn-secondary px-6 py-3 rounded-lg"
              >
                Volver al inicio
              </Link>
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md text-center"
      >
        {/* Logo */}
        <Link to="/" className="flex justify-center mb-12">
          <img src={LOGO_URL} alt="CloudTexa" className="h-12 w-auto" />
        </Link>

        {/* Content */}
        <div className="glass rounded-2xl p-8">
          {renderContent()}
        </div>
      </motion.div>
    </div>
  );
}
